import React, { useState } from 'react';
import { useTwilio } from '../../contexts/TwilioContext';
import styles from './AgentSelectionModal.module.css';

/**
 * Modal mejorado para seleccionar un agente para transferir la llamada
 * Incluye soporte para:
 * - Transferencia fría (cold transfer): transferir directamente
 * - Transferencia cálida (warm transfer): hablar primero antes de transferir
 * - Llamadas internas: llamar a un agente sin transferir
 * 
 * Props:
 * - open: boolean (si el modal está visible)
 * - agents: array (lista de agentes disponibles)
 * - onSelectAgent: función para seleccionar agente (transferencia fría)
 * - onCancel: función para cancelar la selección
 * - mode: 'transfer' | 'call' (modo de operación: transferir o llamar)
 */
export default function AgentSelectionModal({ 
  open, 
  agents = [], 
  onSelectAgent, 
  onCancel,
  mode = 'transfer' // 'transfer' o 'call'
}) {
  const [selectedAgent, setSelectedAgent] = useState(null);
  const { makeCall } = useTwilio();

  const handleAgentSelect = (agent) => {
    // Transferencias solo a agentes disponibles; llamadas internas también pueden esperar.
    if (!agent.conectado) return;
    if (mode === 'transfer' && (agent.estado === 'en_llamada' || agent.disponible === false)) return;
    setSelectedAgent(agent);
  };

  // Iniciar consulta privada o transferencia directa
  const handleTransfer = (warmTransfer) => {
    if (selectedAgent && onSelectAgent) {
      onSelectAgent(selectedAgent, warmTransfer);
      setSelectedAgent(null);
    }
  };

  // Manejar llamada interna (sin transferencia)
  const handleInternalCall = async () => {
    if (!selectedAgent) return;
    
    try {
      console.log('📞 Llamada interna a agente:', selectedAgent);
      
      // Extraer el username del email (antes del @)
      const username = selectedAgent.email.split('@')[0];
      
      console.log('📞 Llamando al agente con username:', username);
      
      // Realizar llamada interna al agente usando solo el username
      await makeCall(username);
      
      // Cerrar el modal después de iniciar la llamada
      handleCancel();
      
    } catch (error) {
      console.error('❌ Error en llamada interna:', error);
      alert('Error al realizar la llamada interna');
    }
  };

  const handleCancel = () => {
    setSelectedAgent(null);
    if (onCancel) {
      onCancel();
    }
  };

  if (!open) return null;
  
  // Separar agentes por estado
  const agentesDisponibles = agents.filter(a => a.conectado !== false && a.estado !== 'en_llamada' && a.disponible !== false);
  const agentesOcupados = agents.filter(a => a.conectado !== false && (a.estado === 'en_llamada' || (a.disponible === false && a.conectado)));
  const agentesDesconectados = agents.filter(a => a.conectado === false);
  
  // Determinar color del indicador de estado
  const getStatusColor = (agent) => {
    if (!agent.conectado) return '#999'; // Desconectado - gris
    
    if (agent.estado === 'en_llamada') return '#ff9800'; // En llamada - naranja
    if (agent.estado === 'disponible') return '#4caf50'; // Disponible - verde
    
    return '#4caf50'; // Por defecto disponible
  };
  
  // Obtener texto del estado
  const getStatusText = (agent) => {
    if (!agent.conectado) return 'Desconectado';
    
    if (agent.estado === 'en_llamada') return 'En llamada';
    if (agent.estado === 'disponible') return 'Disponible';
    
    return agent.disponible ? 'Disponible' : 'Ocupado';
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.icon}>{mode === 'call' ? '📞' : '📞➡️'}</div>
            <div>
              <h3 className={styles.title}>
                {mode === 'call' ? 'Llamar a Compañera' : 'Transferir Llamada'}
              </h3>
              <p className={styles.subtitle}>
                {mode === 'call' ? 'Selecciona una compañera para llamar' : 'Selecciona un agente disponible'}
              </p>
            </div>
          </div>
        </div>

        {/* Lista de agentes */}
        <div className={styles.agentsList}>
          {agents.length === 0 ? (
            <div className={styles.noAgents}>
              <div className={styles.noAgentsIcon}>😔</div>
              <p>No hay agentes en este momento</p>
            </div>
          ) : (
            <>
              {/* Agentes Disponibles */}
              {agentesDisponibles.length > 0 && (
                <>
                  <div className={styles.sectionTitle}>✅ Disponibles ({agentesDisponibles.length})</div>
                  {agentesDisponibles.map((agent) => (
                    <div
                      key={agent.id}
                      className={`${styles.agentItem} ${
                        selectedAgent?.id === agent.id ? styles.selected : ''
                      }`}
                      onClick={() => handleAgentSelect(agent)}
                    >
                      <div className={styles.agentAvatar}>
                        {agent.nombre.charAt(0).toUpperCase()}
                      </div>
                      <div className={styles.agentInfo}>
                        <div className={styles.agentName}>{agent.nombre}</div>
                        <div className={styles.agentStatus}>
                          <span 
                            className={styles.statusIndicator}
                            style={{ backgroundColor: '#4caf50' }}
                          ></span>
                          Disponible
                        </div>
                      </div>
                      <div className={styles.selectIndicator}>
                        {selectedAgent?.id === agent.id && (
                          <div className={styles.checkmark}>✓</div>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Agentes Ocupados (conectados pero en llamada) */}
              {agentesOcupados.length > 0 && (
                <>
                  <div className={styles.sectionTitle}>🔴 Ocupados ({agentesOcupados.length})</div>
                  {agentesOcupados.map((agent) => (
                    <div
                      key={agent.id}
                      className={`${styles.agentItem} ${styles.busy} ${mode === 'call' && selectedAgent?.id === agent.id ? styles.selected : ''}`}
                      title="Este agente está en una llamada activa"
                      onClick={() => mode === 'call' && handleAgentSelect(agent)}
                    >
                      <div className={`${styles.agentAvatar} ${styles.busyAvatar}`}>
                        {agent.nombre.charAt(0).toUpperCase()}
                      </div>
                      <div className={styles.agentInfo}>
                        <div className={styles.agentName}>{agent.nombre}</div>
                        <div className={`${styles.agentStatus} ${styles.busyStatus}`}>
                          <span 
                            className={styles.statusIndicator}
                            style={{ backgroundColor: '#ff5722', animation: 'none' }}
                          ></span>
                          En llamada
                        </div>
                      </div>
                      <div className={styles.selectIndicator}>
                        <span className={styles.busyIcon}>🔴</span>
                      </div>
                    </div>
                  ))}
                </>
              )}
              
              {/* Agentes Desconectados */}
              {agentesDesconectados.length > 0 && (
                <>
                  <div className={styles.sectionTitle}>⚫ Desconectados ({agentesDesconectados.length})</div>
                  {agentesDesconectados.map((agent) => (
                    <div
                      key={agent.id}
                      className={`${styles.agentItem} ${styles.disconnected} ${
                        selectedAgent?.id === agent.id ? styles.selected : ''
                      }`}
                      onClick={() => handleAgentSelect(agent)}
                    >
                      <div className={styles.agentAvatar}>
                        {agent.nombre.charAt(0).toUpperCase()}
                      </div>
                      <div className={styles.agentInfo}>
                        <div className={styles.agentName}>{agent.nombre}</div>
                        <div className={styles.agentStatus}>
                          <span 
                            className={styles.statusIndicator}
                            style={{ backgroundColor: getStatusColor(agent) }}
                          ></span>
                          {getStatusText(agent)}
                        </div>
                      </div>
                      <div className={styles.selectIndicator}>
                        {selectedAgent?.id === agent.id && (
                          <div className={styles.checkmark}>✓</div>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>

        {/* Acciones */}
        <div className={styles.actions}>
          <button 
            className={`${styles.actionButton} ${styles.cancelButton}`} 
            onClick={handleCancel}
          >
            ❌ Cancelar
          </button>
          
          {mode === 'transfer' ? (
            <>
              <button
                className={`${styles.actionButton} ${styles.confirmButton} ${styles.warmTransferButton}`}
                onClick={() => handleTransfer(true)}
                disabled={!selectedAgent || !selectedAgent.conectado || selectedAgent.estado === 'en_llamada'}
                title="Hablar en privado antes de conectar al cliente"
              >
                🔥 Consultar
              </button>
              <button 
                className={`${styles.actionButton} ${styles.confirmButton}`} 
                onClick={() => handleTransfer(false)}
                disabled={!selectedAgent || !selectedAgent.conectado || (mode === 'transfer' && selectedAgent.estado === 'en_llamada')}
                title="Transferir directamente sin consulta"
              >
                📞 Directa
              </button>
            </>
          ) : (
            <>
              {/* Llamada Interna */}
              <button 
                className={`${styles.actionButton} ${styles.confirmButton}`} 
                onClick={handleInternalCall}
                disabled={!selectedAgent || !selectedAgent.conectado}
                title="Llamar al agente internamente"
              >
                📞 Llamar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}